import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import cn from "classnames";
import ResizeHandle from "./ResizeHandle";
import { useResizablePanel } from "../../hooks/useResizablePanel";

// Right-hand side drawer that slides open/closed. The wrapper animates its
// width (so the main pane reflows smoothly) while the fixed-width panel
// slides in from the right. Children stay mounted until the close animation
// finishes, then unmount.
//
// Pass `storageKey` to make the drawer user-resizable (drag its left edge,
// double-click to reset); the chosen width persists in localStorage.
interface Props {
  open: boolean;
  className?: string;
  children: ReactNode;
  storageKey?: string;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
}

const SlideDrawer: React.FC<Props> = ({
  open,
  className,
  children,
  storageKey,
  defaultWidth = 416,
  minWidth = 300,
  maxWidth = 760,
}) => {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(open);
  const ref = useRef<HTMLDivElement>(null);
  const panel = useResizablePanel({
    storageKey: storageKey ?? "pnai-slideDrawerWidth",
    defaultWidth,
    minWidth,
    maxWidth,
    side: "left",
    maxViewportFraction: 0.55,
  });
  const resizable = !!storageKey;
  const width = resizable ? panel.width : defaultWidth;

  useEffect(() => {
    if (open) setMounted(true);
    else setShown(false);
  }, [open]);

  // Once mounted at width 0, force a layout read so the browser has computed
  // that starting width, then expand — this is what makes the transition
  // run (and it doesn't depend on requestAnimationFrame timing).
  useLayoutEffect(() => {
    if (open && mounted && !shown && ref.current) {
      void ref.current.getBoundingClientRect().width;
      setShown(true);
    }
  }, [open, mounted, shown]);

  if (!mounted) return null;

  return (
    <div
      ref={ref}
      style={{ width: shown ? width : 0 }}
      className={cn(
        "shrink-0 overflow-hidden",
        !panel.isResizing && "transition-[width] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]",
      )}
      onTransitionEnd={(e) => {
        if (e.propertyName === "width" && !shown) setMounted(false);
      }}
    >
      <div
        style={{ width }}
        className={cn(
          "relative h-full flex flex-col min-h-0 transition-transform duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]",
          shown ? "translate-x-0" : "translate-x-full",
          className,
        )}
      >
        {resizable && <ResizeHandle edge="left" active={panel.isResizing} {...panel.handleProps} />}
        {children}
      </div>
    </div>
  );
};

export default SlideDrawer;
